(() => {
  'use strict';

  if (window.YYProductGallery?.initialized) return;

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const boundGalleries = new WeakSet();
  const viewerStates = new WeakMap();
  const retryTimers = new WeakMap();
  const userPausedVideos = new WeakSet();

  function getSlider(viewer) {
    return viewer?.slider || viewer?.querySelector?.('[id^="Slider-"]') || null;
  }

  function getSettledItem(viewer) {
    const slider = getSlider(viewer);
    if (!slider) return null;

    const items = Array.from(
      viewer?.sliderItemsToShow?.length
        ? viewer.sliderItemsToShow
        : slider.querySelectorAll('.product__media-item')
    ).filter((item) => item.clientWidth > 0);

    if (!items.length) return null;

    return items.reduce((closest, item) => {
      const itemDistance = Math.abs(item.offsetLeft - slider.scrollLeft);
      const closestDistance = Math.abs(closest.offsetLeft - slider.scrollLeft);
      return itemDistance < closestDistance ? item : closest;
    }, items[0]);
  }

  function getViewerState(viewer) {
    let state = viewerStates.get(viewer);

    if (!state) {
      state = {
        activeItem: null,
        timer: null
      };
      viewerStates.set(viewer, state);
    }

    return state;
  }

  function clearVideoRetries(video) {
    const timers = retryTimers.get(video);
    if (!timers) return;

    timers.forEach((timer) => window.clearTimeout(timer));
    retryTimers.delete(video);
  }

  function configureHostedVideo(video) {
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');

    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    video.autoplay = true;
    video.setAttribute('autoplay', '');

    video.preload = 'metadata';
    video.setAttribute('preload', 'metadata');

    if (mobileQuery.matches) {
      video.controls = false;
      video.removeAttribute('controls');
    }
  }

  function updateToggleButton(video, button) {
    if (!video || !button) return;

    const isPlaying = !video.paused && !video.ended;
    button.dataset.state = isPlaying ? 'playing' : 'paused';
    button.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    button.setAttribute('title', isPlaying ? 'Pause video' : 'Play video');
  }

  function ensureToggleButton(video, deferredMedia) {
    if (!video || !deferredMedia) return null;

    const container = deferredMedia.closest('.product-media-container');
    if (!container) return null;

    let button = container.querySelector('.yy-product-video-toggle');

    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'yy-product-video-toggle';
      button.innerHTML = '<span aria-hidden="true"></span>';
      container.appendChild(button);
    }

    if (button.dataset.yyBound !== 'true') {
      button.dataset.yyBound = 'true';

      button.addEventListener('click', (event) => {
        if (!mobileQuery.matches) return;

        event.preventDefault();
        event.stopPropagation();

        const item = container.closest('.product__media-item');
        const gallery = item?.closest('media-gallery');
        const viewer = gallery?.querySelector('[id^="GalleryViewer"]');

        if (!item || !viewer) return;

        if (video.paused || video.ended) {
          userPausedVideos.delete(video);
          playHostedVideo(video, item, viewer, true);
        } else {
          userPausedVideos.add(video);
          clearVideoRetries(video);
          video.pause();
        }
      });
    }

    if (video.dataset.yyStateBound !== 'true') {
      video.dataset.yyStateBound = 'true';

      ['play', 'pause', 'ended'].forEach((eventName) => {
        video.addEventListener(eventName, () => {
          updateToggleButton(video, button);
        });
      });
    }

    updateToggleButton(video, button);
    return button;
  }

  function ensureHostedVideo(deferredMedia) {
    if (!deferredMedia) return null;

    let video = deferredMedia.querySelector('video');

    if (!video) {
      const template = deferredMedia.querySelector('template');
      const templateVideo = template?.content?.querySelector('video');
      if (!templateVideo) return null;

      video = templateVideo.cloneNode(true);
      configureHostedVideo(video);

      deferredMedia.setAttribute('loaded', 'true');
      deferredMedia.appendChild(video);

      try {
        if (video.readyState === 0) video.load();
      } catch (error) {
        // The browser will continue loading the cloned source normally.
      }
    } else {
      configureHostedVideo(video);
    }

    ensureToggleButton(video, deferredMedia);
    return video;
  }

  function prepareExternalMedia(deferredMedia) {
    const template = deferredMedia?.querySelector('template');
    const iframe = template?.content?.querySelector('iframe');
    if (!iframe?.src) return;

    try {
      const url = new URL(iframe.src, window.location.origin);

      if (iframe.classList.contains('js-youtube')) {
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('mute', '1');
        url.searchParams.set('playsinline', '1');
      }

      if (iframe.classList.contains('js-vimeo')) {
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('muted', '1');
        url.searchParams.set('playsinline', '1');
      }

      iframe.src = url.toString();
    } catch (error) {
      // Keep Shopify's original external-video URL.
    }
  }

  function playIframe(iframe) {
    if (!iframe) return;

    const play = () => {
      if (iframe.classList.contains('js-youtube')) {
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"mute","args":""}',
          '*'
        );
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          '*'
        );
      }

      if (iframe.classList.contains('js-vimeo')) {
        iframe.contentWindow?.postMessage('{"method":"setVolume","value":0}', '*');
        iframe.contentWindow?.postMessage('{"method":"play"}', '*');
      }
    };

    play();
    iframe.addEventListener('load', play, { once: true });
  }

  function pauseIframe(iframe) {
    if (!iframe) return;

    if (iframe.classList.contains('js-youtube')) {
      iframe.contentWindow?.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*'
      );
    }

    if (iframe.classList.contains('js-vimeo')) {
      iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
    }
  }

  function playHostedVideo(video, item, viewer, userInitiated = false) {
    if (!mobileQuery.matches || !video || !item || !viewer) return;
    if (getSettledItem(viewer) !== item) return;
    if (userPausedVideos.has(video) && !userInitiated) return;

    if (userInitiated) userPausedVideos.delete(video);

    configureHostedVideo(video);
    clearVideoRetries(video);

    const tryPlay = () => {
      if (!mobileQuery.matches) return;
      if (getSettledItem(viewer) !== item) return;
      if (userPausedVideos.has(video)) return;
      if (!video.paused && !video.ended) return;

      const promise = video.play();
      if (promise?.catch) promise.catch(() => {});
    };

    tryPlay();

    const timers = [120, 360, 850, 1500].map((delay) =>
      window.setTimeout(tryPlay, delay)
    );
    retryTimers.set(video, timers);

    const playWhenReady = () => tryPlay();
    video.addEventListener('loadeddata', playWhenReady, { once: true });
    video.addEventListener('canplay', playWhenReady, { once: true });
  }

  function pauseItem(item, resetUserPause = false) {
    if (!item) return;

    const video = item.querySelector('deferred-media video');
    if (video) {
      clearVideoRetries(video);
      video.pause();
      if (resetUserPause) userPausedVideos.delete(video);
    }

    pauseIframe(item.querySelector('deferred-media iframe'));
  }

  function pauseOtherItems(gallery, activeItem) {
    gallery?.querySelectorAll('.product__media-item').forEach((item) => {
      if (item !== activeItem) pauseItem(item, true);
    });
  }

  function playItem(item, viewer) {
    if (!mobileQuery.matches || !item || !viewer) return;

    const deferredMedia = item.querySelector('deferred-media');
    if (!deferredMedia) return;

    const templateVideo = deferredMedia.querySelector('template')?.content?.querySelector('video');
    const existingVideo = deferredMedia.querySelector('video');

    if (templateVideo || existingVideo) {
      const video = ensureHostedVideo(deferredMedia);
      if (!video) return;

      userPausedVideos.delete(video);
      playHostedVideo(video, item, viewer);
      return;
    }

    prepareExternalMedia(deferredMedia);

    if (typeof deferredMedia.loadContent === 'function') {
      deferredMedia.loadContent(false);
    }

    playIframe(deferredMedia.querySelector('iframe'));
  }

  function syncViewer(viewer, force = false) {
    if (!mobileQuery.matches || !viewer) return;

    const currentItem = getSettledItem(viewer);
    if (!currentItem) return;

    const state = getViewerState(viewer);
    const changed = state.activeItem !== currentItem;

    if (!changed && !force) return;

    if (changed && state.activeItem) {
      pauseItem(state.activeItem, true);
    }

    state.activeItem = currentItem;

    const gallery = viewer.closest('media-gallery');
    pauseOtherItems(gallery, currentItem);

    if (currentItem.querySelector('deferred-media')) {
      playItem(currentItem, viewer);
    }
  }

  function scheduleSync(viewer, delay = 140, force = false) {
    if (!mobileQuery.matches || !viewer) return;

    const state = getViewerState(viewer);
    if (state.timer) window.clearTimeout(state.timer);

    state.timer = window.setTimeout(() => {
      state.timer = null;
      syncViewer(viewer, force);
    }, delay);
  }

  function bindGallery(gallery) {
    if (!gallery || boundGalleries.has(gallery)) return;

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const slider = getSlider(viewer);
    if (!viewer || !slider) return;

    boundGalleries.add(gallery);

    viewer.addEventListener('slideChanged', () => {
      scheduleSync(viewer, 160);
    });

    slider.addEventListener(
      'scroll',
      () => {
        scheduleSync(viewer, 160);
      },
      { passive: true }
    );

    slider.addEventListener(
      'scrollend',
      () => {
        const state = getViewerState(viewer);
        if (state.timer) window.clearTimeout(state.timer);
        state.timer = null;
        syncViewer(viewer);
      },
      { passive: true }
    );

    slider.addEventListener(
      'touchend',
      () => {
        scheduleSync(viewer, 120);
      },
      { passive: true }
    );

    gallery.addEventListener(
      'click',
      (event) => {
        if (!mobileQuery.matches) return;

        const poster = event.target.closest?.('deferred-media .deferred-media__poster');
        if (!poster) return;

        const deferredMedia = poster.closest('deferred-media');
        const item = poster.closest('.product__media-item');
        if (!deferredMedia || !item) return;

        const templateVideo = deferredMedia.querySelector('template')?.content?.querySelector('video');
        if (!templateVideo) return;

        const video = ensureHostedVideo(deferredMedia);
        if (!video) return;

        userPausedVideos.delete(video);
        getViewerState(viewer).activeItem = item;
        playHostedVideo(video, item, viewer, true);
      },
      true
    );

    gallery.addEventListener('click', (event) => {
      if (!mobileQuery.matches) return;

      const thumbnail = event.target.closest?.('[data-target] button');
      if (!thumbnail) return;

      scheduleSync(viewer, 120, true);
    });

    window.setTimeout(() => {
      syncViewer(viewer);
    }, 0);
  }

  function init(root = document) {
    if (root instanceof Element && root.matches('media-gallery')) {
      bindGallery(root);
    }

    root.querySelectorAll?.('media-gallery').forEach(bindGallery);
  }

  function handleViewportChange(event) {
    document
      .querySelectorAll('.product__media-list .product-media-container.media-type-video video')
      .forEach((video) => {
        if (event.matches) {
          configureHostedVideo(video);
        } else {
          clearVideoRetries(video);
          video.controls = true;
          video.setAttribute('controls', '');
        }
      });

    if (event.matches) {
      document.querySelectorAll('media-gallery').forEach((gallery) => {
        const viewer = gallery.querySelector('[id^="GalleryViewer"]');
        if (viewer) syncViewer(viewer, true);
      });
    }
  }

  function start() {
    init(document);

    document.addEventListener('shopify:section:load', (event) => {
      init(event.target);
    });

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;

          if (node.matches('media-gallery') || node.querySelector('media-gallery')) {
            init(node);
          }
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', handleViewportChange);
    }
  }

  window.YYProductGallery = {
    initialized: true,
    refresh: init
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
